import * as Sentry from "@sentry/react";
import React from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Spacer from "../../../components/Spacer";
import CompetenciesTab from "./CompetenciesTab";
import CreateEditCompetency from "./CreateEditCompetency";

import { useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { rbacPermissionsQuery } from "../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";

interface CompetenciesAdminProps {
	tab: "competencies" | "create" | "edit";
}

export const competenciesLoader = () => {
	const queries: Array<() => SimplifiedQuery> = [() => rbacPermissionsQuery("competencies")];

	return dataLoader([queries]);
};

const CompetenciesAdmin = (props: CompetenciesAdminProps) => {
	const { tab } = props;
	const {
		value: ffCompetenciesEnabled,
		query: { isLoading: isLoadingCompetenciesFF },
	} = useFeatureFlagQuery("ff-is-competency-management-enabled", false);
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("competencies");

	const isLoadingAny = isLoadingCompetenciesFF || isRbacLoading;

	const hasViewPermission = rbacPermissions?.includes("competencies:viewAdmin");

	const renderInfoBox = (heading: string, message: string) => (
		<div>
			<AdminTitle includeSpacer={false}>{heading}</AdminTitle>
			<div className="bg-py-primary-1 flex w-[400px] gap-1.5 rounded-sm p-5">
				<FontAwesomeIcon icon={faInfoCircle} className="text-py-primary-7 text-lg" />
				<div className="font-semibold">{message}</div>
			</div>
		</div>
	);

	const renderGatedContent = (): React.ReactNode => {
		if (isLoadingAny) return null;
		if (!ffCompetenciesEnabled) {
			return renderInfoBox("Competencies", "This feature is not enabled for your organization.");
		}
		if (!hasViewPermission) {
			return renderInfoBox("Competencies", "Missing required permissions.");
		}
		switch (tab) {
			case "competencies":
				return (
					<>
						<Spacer height={4} />
						<CompetenciesTab />
					</>
				);
			case "create":
				return <CreateEditCompetency mode="create" />;
			case "edit":
				return <CreateEditCompetency mode="edit" />;
		}
	};

	// Create/edit pages use a full-bleed neutral-1 background (centered form card
	// on a grey page) so the bg extends edge-to-edge under the navbar / sidebar.
	// List + gating states use the standard AdminContainer max-width grid.
	const isFormPage = tab === "create" || tab === "edit";
	const useFullBleed = isFormPage && ffCompetenciesEnabled && !isLoadingAny && hasViewPermission;

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			{useFullBleed ? (
				<>
					{/* Fixed underlay paints bg-neutral-1 across the full viewport
					    regardless of content height. Navbar / sidebar / form card
					    have their own backgrounds that cover the underlay. */}
					<div className="fixed inset-0 -z-10 bg-neutral-1" aria-hidden="true" />
					{renderGatedContent()}
				</>
			) : (
				<AdminContainer>{renderGatedContent()}</AdminContainer>
			)}
		</Sentry.ErrorBoundary>
	);
};

export default CompetenciesAdmin;
